import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Table, TableBody, TableHead, TableRow, Typography } from '@mui/material';
import { IModalProps } from 'components/Modal';
import MediaTypography from 'components/MediaTypography';

import { IStudentLessonModel } from '@common/models/StudentLessons/Studentlesson';

import {
	StyledCriteriaCell,
	StyledCriteriaTitle,
	StyledMarkCell,
	StyledRubricLevelCell,
	StyledRubricModal,
	StyledRubricTableContainer,
} from './styled';

interface PropsType extends Omit<IModalProps, 'children' | 'title'> {
	lessonModel: IStudentLessonModel;
}

function RubricModal({ lessonModel, ...props }: PropsType) {
	const { t } = useTranslation();
	const selectedRubricLevelIds = new Set(lessonModel.rubricLevels.map(({ id }) => id));
	const shouldShowGroupNames = lessonModel.rubricUnions.length > 1;

	return (
		<StyledRubricModal title={t('lesson.rubric.tableTitle')} {...props}>
			<Box display="flex" flexDirection="column" gap="24px">
				{lessonModel.rubricUnions.map((rubricUnion) => {
					const rubricLevels = rubricUnion.rubrics[0]?.rubricLevels;

					if (!rubricLevels?.length) {
						return null;
					}

					return (
						<Box key={rubricUnion.id} display="flex" flexDirection="column" gap="8px">
							{shouldShowGroupNames && rubricUnion.name && (
								<MediaTypography variant="h3">{rubricUnion.name}</MediaTypography>
							)}

							<StyledRubricTableContainer>
								<Table
									size="small"
									aria-label={rubricUnion.name || t('lesson.rubric.tableTitle')}
									sx={{ minWidth: 356 + rubricLevels.length * 220 }}
								>
									<TableHead>
										<TableRow>
											<StyledCriteriaCell>
												<Typography variant="subtitle1">
													{t('lesson.rubric.criteria')}
												</Typography>
											</StyledCriteriaCell>
											{rubricLevels.map((rubricLevel) => (
												<StyledRubricLevelCell key={rubricLevel.id} isSelected={false}>
													<Typography variant="subtitle1">{rubricLevel.title}</Typography>
													<Typography variant="subtitle1">
														({rubricLevel.percent_from}%-{rubricLevel.percent_to}%)
													</Typography>
												</StyledRubricLevelCell>
											))}
											<StyledMarkCell>
												<Typography variant="subtitle1">
													{t('lesson.rubric.mark')}
												</Typography>
											</StyledMarkCell>
										</TableRow>
									</TableHead>

									<TableBody>
										{rubricUnion.rubrics.map((rubric) => (
											<TableRow key={rubric.id}>
												<StyledCriteriaCell>
													<StyledCriteriaTitle>
														<Typography variant="subtitle1" marginBottom="8px">
															{rubric.title}
														</Typography>
													</StyledCriteriaTitle>
													<Typography variant="body2">{rubric.description}</Typography>
												</StyledCriteriaCell>

												{rubric.rubricLevels.map((rubricLevel) => (
													<StyledRubricLevelCell
														key={rubricLevel.id}
														isSelected={selectedRubricLevelIds.has(rubricLevel.id)}
													>
														<Typography variant="body2">{rubricLevel.description}</Typography>
													</StyledRubricLevelCell>
												))}

												<StyledMarkCell>
													<Typography variant="subtitle1">{rubric.grade}</Typography>
												</StyledMarkCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</StyledRubricTableContainer>
						</Box>
					);
				})}
			</Box>
		</StyledRubricModal>
	);
}

export default RubricModal;
